import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { acceptCreatorAgreement } from "@/lib/api/users";

// ─── Theme & Design Tokens ────────────────────────────────────────────────────
const theme = {
  gold: "#C9A84C",
  goldLight: "#E4C97A",
  goldDim: "#8A6E32",
  bg: "#080808",
  surface: "#111111",
  surfaceHigh: "#181818",
  border: "#222222",
  borderGold: "rgba(201,168,76,0.25)",
  text: "#F0EDE8",
  textMuted: "#888880",
  textDim: "#444440",
  red: "#C04040",
  green: "#4A8C5C",
};

// ─── Styles (inline, no Tailwind compiler needed) ─────────────────────────────
const css = {
  root: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    background: theme.bg,
    color: theme.text,
    minHeight: "100vh",
    maxWidth: 480,
    margin: "0 auto",
    position: "relative",
    overflowX: "hidden",
  },
  // Header
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "rgba(8,8,8,0.92)",
    backdropFilter: "blur(12px)",
    borderBottom: `1px solid ${theme.borderGold}`,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logoMark: {
    width: 32,
    height: 32,
    background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDim})`,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 900,
    color: "#000",
    letterSpacing: "-1px",
    flexShrink: 0,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.text,
    letterSpacing: "0.04em",
    fontFamily: "'Georgia', serif",
  },
  headerSub: {
    fontSize: 10,
    color: theme.gold,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: "'Georgia', serif",
  },
  // Progress
  progressBar: {
    height: 2,
    background: theme.border,
    position: "relative",
  },
  progressFill: (pct) => ({
    height: "100%",
    background: `linear-gradient(90deg, ${theme.goldDim}, ${theme.gold})`,
    width: `${pct}%`,
    transition: "width 0.5s ease",
  }),
  // Step Indicator
  stepRow: {
    display: "flex",
    padding: "16px 20px 0",
    gap: 6,
    justifyContent: "center",
  },
  stepDot: (active, done) => ({
    height: 3,
    flex: 1,
    maxWidth: 60,
    borderRadius: 2,
    background: done ? theme.gold : active ? theme.goldDim : theme.border,
    transition: "background 0.4s ease",
  }),
  // Section
  section: {
    padding: "20px 20px 0",
  },
  sectionLabel: {
    fontSize: 10,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: theme.gold,
    fontFamily: "'Georgia', serif",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 400,
    color: theme.text,
    lineHeight: 1.3,
    marginBottom: 4,
  },
  sectionSub: {
    fontSize: 13,
    color: theme.textMuted,
    lineHeight: 1.6,
    marginBottom: 20,
  },
  // Clause Cards
  clauseCard: (expanded) => ({
    background: expanded ? theme.surfaceHigh : theme.surface,
    border: `1px solid ${expanded ? theme.borderGold : theme.border}`,
    borderRadius: 10,
    marginBottom: 8,
    overflow: "hidden",
    transition: "all 0.3s ease",
  }),
  clauseHeader: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    cursor: "pointer",
    gap: 12,
    userSelect: "none",
  },
  clauseNum: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${theme.goldDim}, ${theme.gold}20)`,
    border: `1px solid ${theme.goldDim}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    color: theme.gold,
    fontWeight: 700,
    flexShrink: 0,
    fontFamily: "'Georgia', serif",
  },
  clauseTitle: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: theme.text,
    letterSpacing: "0.02em",
    fontFamily: "'Georgia', serif",
  },
  clauseChevron: (open) => ({
    fontSize: 10,
    color: theme.gold,
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.3s ease",
    flexShrink: 0,
  }),
  clauseBody: {
    padding: "0 16px 14px",
    fontSize: 12,
    color: theme.textMuted,
    lineHeight: 1.75,
    borderTop: `1px solid ${theme.border}`,
  },
  clauseHighlight: {
    background: `${theme.gold}12`,
    border: `1px solid ${theme.goldDim}40`,
    borderRadius: 6,
    padding: "10px 12px",
    marginTop: 10,
    fontSize: 12,
    color: theme.goldLight,
    lineHeight: 1.6,
  },
  // Form
  fieldGroup: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.textMuted,
    marginBottom: 6,
    display: "block",
    fontFamily: "'Georgia', serif",
  },
  fieldRequired: {
    color: theme.gold,
    marginLeft: 2,
  },
  input: {
    width: "100%",
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: "12px 14px",
    fontSize: 14,
    color: theme.text,
    outline: "none",
    fontFamily: "'Georgia', serif",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  inputFocus: {
    borderColor: theme.goldDim,
  },
  select: {
    width: "100%",
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: "12px 14px",
    fontSize: 14,
    color: theme.text,
    outline: "none",
    fontFamily: "'Georgia', serif",
    boxSizing: "border-box",
    appearance: "none",
    cursor: "pointer",
  },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  // Signature pad
  sigPadWrap: {
    border: `1px solid ${theme.borderGold}`,
    borderRadius: 10,
    overflow: "hidden",
    background: theme.surface,
    position: "relative",
  },
  sigPadCanvas: {
    display: "block",
    width: "100%",
    height: 140,
    cursor: "crosshair",
    touchAction: "none",
  },
  sigPadLabel: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: 12,
    color: theme.textDim,
    pointerEvents: "none",
    fontStyle: "italic",
    textAlign: "center",
    lineHeight: 1.6,
  },
  sigActions: {
    display: "flex",
    gap: 8,
    marginTop: 8,
  },
  // Checkbox toggle
  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "14px 16px",
    background: theme.surface,
    borderRadius: 10,
    border: `1px solid ${theme.border}`,
    cursor: "pointer",
    marginBottom: 10,
  },
  checkBox: (checked) => ({
    width: 20,
    height: 20,
    borderRadius: 4,
    border: `2px solid ${checked ? theme.gold : theme.border}`,
    background: checked ? `${theme.gold}20` : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 1,
    transition: "all 0.2s",
  }),
  checkText: {
    fontSize: 12,
    color: theme.textMuted,
    lineHeight: 1.6,
  },
  checkTextBold: {
    color: theme.text,
    fontSize: 13,
    fontWeight: 600,
    display: "block",
    marginBottom: 2,
    fontFamily: "'Georgia', serif",
  },
  // Buttons
  btnPrimary: {
    width: "100%",
    padding: "16px",
    background: `linear-gradient(135deg, ${theme.goldDim}, ${theme.gold})`,
    border: "none",
    borderRadius: 10,
    color: "#000",
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    cursor: "pointer",
    fontFamily: "'Georgia', serif",
    transition: "opacity 0.2s",
  },
  btnSecondary: {
    width: "100%",
    padding: "14px",
    background: "transparent",
    border: `1px solid ${theme.border}`,
    borderRadius: 10,
    color: theme.textMuted,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "'Georgia', serif",
    transition: "border-color 0.2s",
  },
  btnSmall: {
    padding: "7px 14px",
    background: "transparent",
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    color: theme.textMuted,
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "'Georgia', serif",
  },
  btnRow: {
    display: "flex",
    gap: 10,
    padding: "20px",
    paddingTop: 16,
  },
  // Success
  successWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 24px",
    textAlign: "center",
  },
  successIcon: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: `${theme.gold}15`,
    border: `2px solid ${theme.gold}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
    marginBottom: 24,
  },
  // Key terms summary card
  termCard: {
    background: theme.surface,
    border: `1px solid ${theme.borderGold}`,
    borderRadius: 10,
    padding: "16px",
    marginBottom: 10,
  },
  termRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    paddingBottom: 10,
    marginBottom: 10,
    borderBottom: `1px solid ${theme.border}`,
  },
  termKey: {
    fontSize: 11,
    color: theme.textMuted,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    fontFamily: "'Georgia', serif",
  },
  termVal: {
    fontSize: 13,
    color: theme.goldLight,
    fontWeight: 600,
    textAlign: "right",
    maxWidth: "55%",
    fontFamily: "'Georgia', serif",
  },
  divider: {
    height: 1,
    background: theme.border,
    margin: "20px 0",
  },
  spacer: (h) => ({ height: h }),
};

// ─── Clause Data ──────────────────────────────────────────────────────────────
const clauses = [
  {
    num: "§3",
    title: "Grant of Rights",
    body: "You grant BinyumaTV a non-exclusive, worldwide, sublicensable, royalty-free license to reproduce, distribute, display, transmit, market, promote, monetize, edit, format, subtitle, dub, and otherwise exploit your Licensed Programs across AVOD, SVOD, and TVOD models.",
    highlight: "Non-exclusive — you retain ownership of your content.",
  },
  {
    num: "§6",
    title: "Exclusivity",
    body: "VURT Originals (content premiering on the platform) are exclusive for 12 months from first exhibition. If VURT provides any financing or minimum guarantee, the exclusivity extends to a minimum of 24 months.",
    highlight: "12 months exclusivity for self-funded content. 24 months if VURT co-finances.",
  },
  {
    num: "§7",
    title: "Term & Termination",
    body: "The agreement runs for 2 years from first exhibition, then continues month-to-month. VURT may terminate for convenience with 60 days' notice. You may not terminate for convenience during an exclusivity period.",
    highlight: "2-year initial term, rolling monthly thereafter.",
  },
  {
    num: "§11–12",
    title: "Revenue Share",
    body: "You receive 50% of Net Revenue, paid quarterly (minimum $1,000 threshold). Net Revenue is gross receipts minus app store fees, payment processing, ad-tech fees, CDN/hosting costs, taxes, and music/rights payments.",
    highlight: "50/50 split of net revenue after platform operating costs.",
  },
  {
    num: "§8",
    title: "Content Withdrawal",
    body: "You may request withdrawal in writing with 60 days' notice. VURT must comply within a commercially reasonable time — but no less than 90 days for actively monetized content.",
    highlight: "90-day wind-down period for monetized content.",
  },
  {
    num: "§14",
    title: "Data Ownership",
    body: "All platform and user data is owned exclusively by VURT. You receive view-only access to certain performance metrics on a quarterly basis.",
    highlight: "Performance data visibility quarterly — full data owned by VURT.",
  },
  {
    num: "§19–20",
    title: "Warranties & Indemnification",
    body: "You represent that you own all rights to your content, it does not infringe any third-party IP, and you are responsible for all talent, guild, music, and third-party payments. You indemnify VURT against all claims arising from your content.",
    highlight: "You bear full responsibility for clearing rights in your content.",
  },
  {
    num: "§25",
    title: "Governing Law",
    body: "This agreement is governed by Florida law. Any dispute is resolved by binding arbitration in Miami-Dade County, Florida.",
    highlight: "Arbitration in Miami, FL — not litigation.",
  },
];

// ─── Signature Canvas ──────────────────────────────────────────────────────────
function SignaturePad({ onSign, signed }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(signed);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  };

  const start = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    drawing.current = true;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = theme.gold;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (!hasSig) {
      setHasSig(true);
      onSign(true);
    }
  };

  const end = (e) => {
    e.preventDefault();
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
    onSign(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth * window.devicePixelRatio || 440;
    canvas.height = 140 * window.devicePixelRatio || 140;
    const ctx = canvas.getContext("2d");
    ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
  }, []);

  return (
    <div>
      <div style={css.sigPadWrap}>
        <canvas
          ref={canvasRef}
          style={css.sigPadCanvas}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={end}
        />
        {!hasSig && (
          <div style={css.sigPadLabel}>
            Sign here
            <br />
            <span style={{ fontSize: 10, opacity: 0.6 }}>draw with finger or mouse</span>
          </div>
        )}
      </div>
      <div style={css.sigActions}>
        <button style={css.btnSmall} onClick={clear}>
          ↺ Clear
        </button>
        {hasSig && (
          <span style={{ fontSize: 11, color: theme.green, padding: "7px 0", display: "flex", alignItems: "center", gap: 4 }}>
            ✓ Signature captured
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Clause Card ──────────────────────────────────────────────────────────────
function ClauseCard({ clause, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={css.clauseCard(open)}>
      <div style={css.clauseHeader} onClick={() => setOpen(!open)}>
        <div style={css.clauseNum}>{index + 1}</div>
        <span style={css.clauseTitle}>{clause.title}</span>
        <span style={css.clauseChevron(open)}>▼</span>
      </div>
      {open && (
        <div style={css.clauseBody}>
          <p style={{ margin: "10px 0" }}>{clause.body}</p>
          <div style={css.clauseHighlight}>⚡ {clause.highlight}</div>
        </div>
      )}
    </div>
  );
}

// ─── Input with focus state ────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div style={css.fieldGroup}>
      <label style={css.fieldLabel}>
        {label}
        {required && <span style={css.fieldRequired}> *</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      style={{ ...css.input, ...(focused ? css.inputFocus : {}) }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

// ─── Check Item ───────────────────────────────────────────────────────────────
function CheckItem({ title, body, checked, onToggle }) {
  return (
    <div style={css.checkRow} onClick={onToggle}>
      <div style={css.checkBox(checked)}>{checked && <span style={{ color: theme.gold, fontSize: 11, fontWeight: 700 }}>✓</span>}</div>
      <div style={css.checkText}>
        <span style={css.checkTextBold}>{title}</span>
        {body}
      </div>
    </div>
  );
}

// ─── Steps ────────────────────────────────────────────────────────────────────
const STEPS = ["Overview", "Key Terms", "Your Details", "Review & Sign"];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CreatorAgreement() {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [signed, setSigned] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const scrollRef = useRef(null);

  const [checks, setChecks] = useState({
    rights: false,
    revenue: false,
    exclusivity: false,
    terms: false,
  });

  const [form, setForm] = useState({
    legalName: "",
    entityType: "Individual",
    email: "",
    phone: "",
    address: "",
    seriesTitle: "",
    genre: "",
    episodes: "",
    language: "",
    country: "",
    vurtOriginal: "No",
  });

  // Prefill from authenticated user
  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      legalName: prev.legalName || user.displayName || user.name || "",
      email: prev.email || user.email || "",
    }));
  }, [user]);

  const allChecked = Object.values(checks).every(Boolean);
  const formValid =
    form.legalName && form.email && form.seriesTitle && form.episodes && form.language && form.country;
  const canSubmit = allChecked && signed && formValid;

  const goNext = () => {
    setStep((s) => Math.min(s + 1, 3));
    setTimeout(() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    setTimeout(() => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCheck = (k) => () => setChecks((c) => ({ ...c, [k]: !c[k] }));

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await acceptCreatorAgreement();
      await refreshUser();
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = submitted ? 100 : ((step + 1) / 4) * 100;

  // ── Submitted State ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={css.root}>
        <div style={{ ...css.header }}>
          <div style={css.logoMark}>B</div>
          <div style={css.headerText}>
            <div style={css.headerTitle}>BinyumaTV</div>
            <div style={css.headerSub}>Creator Agreement</div>
          </div>
        </div>
        <div style={css.progressBar}>
          <div style={css.progressFill(100)} />
        </div>
        <div style={css.successWrap}>
          <div style={css.successIcon}>✦</div>
          <div style={{ fontSize: 11, letterSpacing: "0.15em", color: theme.gold, textTransform: "uppercase", marginBottom: 10 }}>
            Agreement Signed
          </div>
          <h2 style={{ fontSize: 26, fontWeight: 400, marginBottom: 12, color: theme.text }}>
            Welcome to BinyumaTV
          </h2>
          <p style={{ fontSize: 13, color: theme.textMuted, lineHeight: 1.7, maxWidth: 320, marginBottom: 32 }}>
            Your distribution agreement for <strong style={{ color: theme.text }}>{form.seriesTitle}</strong> has been
            submitted. Our team will review and countersign within 3–5 business days.
          </p>

          <div style={{ ...css.termCard, width: "100%", boxSizing: "border-box", textAlign: "left" }}>
            <div style={{ ...css.termRow, borderBottom: `1px solid ${theme.border}`, paddingBottom: 10, marginBottom: 10 }}>
              <span style={css.termKey}>Creator</span>
              <span style={css.termVal}>{form.legalName}</span>
            </div>
            <div style={{ ...css.termRow, borderBottom: `1px solid ${theme.border}`, paddingBottom: 10, marginBottom: 10 }}>
              <span style={css.termKey}>Series</span>
              <span style={css.termVal}>{form.seriesTitle}</span>
            </div>
            <div style={{ ...css.termRow, borderBottom: `1px solid ${theme.border}`, paddingBottom: 10, marginBottom: 10 }}>
              <span style={css.termKey}>Revenue Share</span>
              <span style={css.termVal}>50% Net</span>
            </div>
            <div style={{ ...css.termRow, borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
              <span style={css.termKey}>Exclusivity</span>
              <span style={css.termVal}>{form.vurtOriginal === "Yes" ? "12 months" : "Standard"}</span>
            </div>
          </div>

          <p style={{ fontSize: 11, color: theme.textDim, marginTop: 24, lineHeight: 1.6 }}>
            A copy of this agreement will be sent to{" "}
            <span style={{ color: theme.textMuted }}>{form.email}</span>
          </p>

          <div style={{ ...css.divider, width: "100%" }} />

          <a
            href="mailto:submissions@myvurt.com"
            style={{ fontSize: 12, color: theme.gold, textDecoration: "none", letterSpacing: "0.04em" }}
          >
            submissions@myvurt.com
          </a>
          <p style={{ fontSize: 11, color: theme.textDim, marginTop: 6 }}>myvurt.com · 786-748-2517</p>
        </div>
      </div>
    );
  }

  // ── Step 0: Overview ─────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <>
      <div style={css.section}>
        <div style={css.sectionLabel}>Digital Distribution</div>
        <h1 style={css.sectionTitle}>Creator License Agreement</h1>
        <p style={css.sectionSub}>
          Review the terms of your distribution deal with BinyumaTV before signing. This is a legally binding agreement — take a moment to understand what you're agreeing to.
        </p>

        <div style={css.termCard}>
          {[
            ["Revenue Split", "50% of Net Revenue"],
            ["Territory", "Worldwide"],
            ["Term", "2 Years from First Exhibition"],
            ["Exclusivity", "12 months (VURT Originals)"],
            ["Payment", "Quarterly · Min. $1,000"],
            ["Governing Law", "Florida, USA"],
          ].map(([k, v], i, arr) => (
            <div
              key={k}
              style={{
                ...css.termRow,
                ...(i === arr.length - 1 ? { borderBottom: "none", paddingBottom: 0, marginBottom: 0 } : {}),
              }}
            >
              <span style={css.termKey}>{k}</span>
              <span style={css.termVal}>{v}</span>
            </div>
          ))}
        </div>

        <div
          style={{
            background: `${theme.red}12`,
            border: `1px solid ${theme.red}40`,
            borderRadius: 8,
            padding: "12px 14px",
            fontSize: 12,
            color: "#E07070",
            lineHeight: 1.7,
            marginBottom: 20,
          }}
        >
          <strong style={{ display: "block", marginBottom: 4 }}>⚠ Important</strong>
          This agreement is not managed by Anthropic or BinyumaTV's AI tools. It is a legally binding document. Consider consulting a lawyer if you have questions.
        </div>
      </div>
      <div style={css.btnRow}>
        <button style={css.btnPrimary} onClick={goNext}>
          Read the Agreement →
        </button>
      </div>
    </>
  );

  // ── Step 1: Key Terms ─────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <>
      <div style={css.section}>
        <div style={css.sectionLabel}>Agreement Terms</div>
        <h2 style={css.sectionTitle}>Key Clauses</h2>
        <p style={css.sectionSub}>Tap each section to expand. Read all before proceeding.</p>
        {clauses.map((c, i) => (
          <ClauseCard key={c.num} clause={c} index={i} />
        ))}
        <div style={{ height: 4 }} />
      </div>
      <div style={css.btnRow}>
        <button style={{ ...css.btnSecondary, flex: 0.4 }} onClick={goBack}>
          ← Back
        </button>
        <button style={{ ...css.btnPrimary, flex: 1 }} onClick={goNext}>
          Continue →
        </button>
      </div>
    </>
  );

  // ── Step 2: Creator Details ───────────────────────────────────────────────────
  const renderStep2 = () => (
    <>
      <div style={css.section}>
        <div style={css.sectionLabel}>Your Information</div>
        <h2 style={css.sectionTitle}>Creator Details</h2>
        <p style={css.sectionSub}>These details will appear on your signed agreement.</p>

        <div
          style={{
            fontSize: 11,
            color: theme.gold,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 12,
            marginTop: 4,
          }}
        >
          — Licensor Information
        </div>

        <Field label="Legal Name" required>
          <TextInput value={form.legalName} onChange={setField("legalName")} placeholder="Full legal name or entity" />
        </Field>

        <Field label="Entity Type">
          <select
            style={css.select}
            value={form.entityType}
            onChange={(e) => setField("entityType")(e.target.value)}
          >
            <option>Individual</option>
            <option>LLC</option>
            <option>Corporation</option>
            <option>Partnership</option>
          </select>
        </Field>

        <div style={css.row2}>
          <Field label="Email" required>
            <TextInput value={form.email} onChange={setField("email")} placeholder="you@email.com" type="email" />
          </Field>
          <Field label="Phone">
            <TextInput value={form.phone} onChange={setField("phone")} placeholder="+1 000 000" />
          </Field>
        </div>

        <Field label="Mailing Address">
          <TextInput value={form.address} onChange={setField("address")} placeholder="Street, City, Country" />
        </Field>

        <div
          style={{
            fontSize: 11,
            color: theme.gold,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            margin: "20px 0 12px",
          }}
        >
          — Program Details
        </div>

        <Field label="Series / Film Title" required>
          <TextInput value={form.seriesTitle} onChange={setField("seriesTitle")} placeholder="Your content title" />
        </Field>

        <div style={css.row2}>
          <Field label="Genre">
            <select
              style={css.select}
              value={form.genre}
              onChange={(e) => setField("genre")(e.target.value)}
            >
              <option value="">Select</option>
              <option>Drama</option>
              <option>Comedy</option>
              <option>Thriller</option>
              <option>Romance</option>
              <option>Documentary</option>
              <option>Action</option>
              <option>Horror</option>
              <option>Other</option>
            </select>
          </Field>
          <Field label="# of Episodes" required>
            <TextInput value={form.episodes} onChange={setField("episodes")} placeholder="e.g. 10" type="number" />
          </Field>
        </div>

        <div style={css.row2}>
          <Field label="Language" required>
            <TextInput value={form.language} onChange={setField("language")} placeholder="e.g. Luganda" />
          </Field>
          <Field label="Country" required>
            <TextInput value={form.country} onChange={setField("country")} placeholder="e.g. Uganda" />
          </Field>
        </div>

        <Field label="VURT Original?">
          <select
            style={css.select}
            value={form.vurtOriginal}
            onChange={(e) => setField("vurtOriginal")(e.target.value)}
          >
            <option>No</option>
            <option>Yes</option>
          </select>
        </Field>

        <div style={{ height: 4 }} />
      </div>
      <div style={css.btnRow}>
        <button style={{ ...css.btnSecondary, flex: 0.4 }} onClick={goBack}>
          ← Back
        </button>
        <button
          style={{ ...css.btnPrimary, flex: 1, opacity: formValid ? 1 : 0.4 }}
          onClick={formValid ? goNext : undefined}
        >
          Continue →
        </button>
      </div>
    </>
  );

  // ── Step 3: Review & Sign ─────────────────────────────────────────────────────
  const renderStep3 = () => (
    <>
      <div style={css.section}>
        <div style={css.sectionLabel}>Final Step</div>
        <h2 style={css.sectionTitle}>Review & Sign</h2>
        <p style={css.sectionSub}>Confirm your acknowledgements, then sign below.</p>

        <CheckItem
          title="I own all rights to this content"
          body="Including copyright, music sync, and performer clearances."
          checked={checks.rights}
          onToggle={toggleCheck("rights")}
        />
        <CheckItem
          title="I understand the 50/50 revenue split"
          body="Net revenue after app store fees, hosting, ad-tech, and platform costs."
          checked={checks.revenue}
          onToggle={toggleCheck("revenue")}
        />
        <CheckItem
          title="I accept the exclusivity terms"
          body="12 months for VURT Originals. 24 months if VURT co-finances."
          checked={checks.exclusivity}
          onToggle={toggleCheck("exclusivity")}
        />
        <CheckItem
          title="I have read and agree to all terms"
          body="This is a legally binding agreement governed by Florida law."
          checked={checks.terms}
          onToggle={toggleCheck("terms")}
        />

        <div style={{ ...css.divider, margin: "20px 0" }} />

        <div style={{ marginBottom: 6 }}>
          <span style={css.sectionLabel}>Your Signature</span>
        </div>

        <SignaturePad onSign={setSigned} signed={signed} />

        <div style={{ ...css.divider, margin: "20px 0" }} />

        <div style={css.termCard}>
          <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Agreement Summary
          </div>
          {[
            ["Creator", form.legalName || "—"],
            ["Entity", form.entityType],
            ["Series", form.seriesTitle || "—"],
            ["Episodes", form.episodes || "—"],
            ["Language", form.language || "—"],
            ["Country", form.country || "—"],
            ["Revenue", "50% Net"],
            ["Territory", "Worldwide"],
          ].map(([k, v], i, arr) => (
            <div
              key={k}
              style={{
                ...css.termRow,
                ...(i === arr.length - 1 ? { borderBottom: "none", paddingBottom: 0, marginBottom: 0 } : {}),
              }}
            >
              <span style={css.termKey}>{k}</span>
              <span style={{ ...css.termVal, color: theme.text }}>{v}</span>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 11, color: theme.textDim, lineHeight: 1.7, marginTop: 14, marginBottom: 4 }}>
          By signing, you confirm you have read, understood, and agree to the BinyumaTV Digital Distribution License Agreement in its entirety. Effective date will be set upon countersignature.
        </p>

        <div style={{ height: 4 }} />
      </div>

      {submitError && (
        <p style={{ fontSize: 12, color: theme.red, marginBottom: 12, padding: "0 20px" }}>
          {submitError}
        </p>
      )}

      <div style={css.btnRow}>
        <button style={{ ...css.btnSecondary, flex: 0.4 }} onClick={goBack} disabled={submitting}>
          ← Back
        </button>
        <button
          style={{
            ...css.btnPrimary,
            flex: 1,
            opacity: canSubmit && !submitting ? 1 : 0.35,
            cursor: canSubmit && !submitting ? "pointer" : "not-allowed",
          }}
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? "Signing…" : canSubmit ? "Sign & Submit ✦" : "Complete All Fields"}
        </button>
      </div>
    </>
  );

  const steps = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <div style={css.root}>
      {/* Header */}
      <div style={css.header}>
        <div style={css.logoMark}>B</div>
        <div style={css.headerText}>
          <div style={css.headerTitle}>BinyumaTV</div>
          <div style={css.headerSub}>Creator Agreement</div>
        </div>
        <span style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Georgia', serif" }}>
          {step + 1} / {STEPS.length}
        </span>
      </div>

      {/* Progress */}
      <div style={css.progressBar}>
        <div style={css.progressFill(progressPct)} />
      </div>

      {/* Step dots */}
      <div style={css.stepRow}>
        {STEPS.map((_, i) => (
          <div key={i} style={css.stepDot(i === step, i < step)} />
        ))}
      </div>

      {/* Content */}
      <div ref={scrollRef} style={{ overflowY: "auto" }}>
        {steps[step]()}
      </div>

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          padding: "0 20px 28px",
          fontSize: 10,
          color: theme.textDim,
          letterSpacing: "0.04em",
          lineHeight: 1.8,
        }}
      >
        VURT Corporation · Confidential
        <br />
        submissions@myvurt.com · myvurt.com
      </div>
    </div>
  );
}
